import { APP_CHAIN, BASE_TOKEN_CONFIG, REFETCH_INTERVAL_FAST } from '../confs/app';
import { publicClient } from '../utils/chain-client';
import { resolveWalletAddress } from '../utils/wallet-address';
import { useAuth } from './use-auth';
import { useSignedUserInfo } from './use-signed-user-info';
import { useBaseTokens } from '@/libs/react-sdk/api/use-base-tokens';
import { ApiInstance } from '@/libs/sdk/api';
import { useQuery } from '@tanstack/react-query';
import { Address, Chain, erc20Abi } from 'viem';
import { multicall } from 'viem/actions';

type TokenBalanceParams = {
  chain?: Chain;
};
export const useSingerTokenBalance = (api: ApiInstance, { chain = APP_CHAIN }: TokenBalanceParams = {}) => {
  const { linkedWallet } = useAuth();
  const { data: userInfo } = useSignedUserInfo(api);
  // balanceOf must target the proven wallet — userInfo.address is the identity
  // key (did:… for DID users), which silently read 0 for every linked DID user.
  const address = resolveWalletAddress(linkedWallet, userInfo?.address);
  const { data: tokens } = useBaseTokens(api);
  const query = useQuery({
    enabled: !!address && !!tokens,
    refetchInterval: REFETCH_INTERVAL_FAST, // TODO remove refetchInterval
    queryKey: ['SingerTokenBalance', address, tokens, chain?.id],
    queryFn: async () => {
      if (!tokens?.baseTokens) return [];
      // Belt-and-suspenders: union the FE's configured base tokens (USDT + AIP) so
      // the multicall covers every collateral even if the backend list lags behind.
      const seen = new Set(tokens.baseTokens.map(t => t.baseTokenAddress?.toLowerCase()));
      const merged = [
        ...tokens.baseTokens,
        ...BASE_TOKEN_CONFIG.filter(c => c.address && !seen.has(c.address.toLowerCase())).map(c => ({
          contrractType: '',
          baseTokenName: c.name,
          baseTokenSymbol: c.symbol,
          baseTokenAddress: c.address,
          baseTokenDecimals: c.decimals,
        })),
      ];
      // Filter out tokens without valid addresses (e.g. Points token)
      const validTokens = merged.filter(token => token.baseTokenAddress && token.baseTokenAddress.startsWith('0x'));
      if (validTokens.length === 0) return merged.map(token => ({ ...token, balance: 0n }));
      const result = await multicall(publicClient, {
        allowFailure: true,
        contracts: validTokens.map(token => ({
          address: token.baseTokenAddress as Address,
          abi: erc20Abi,
          functionName: 'balanceOf',
          args: [address],
        })),
      });
      // A failed balanceOf on a base token this app actually configures is an RPC
      // problem, not a zero balance: mapping it to 0n made RPC hiccups render as
      // a fake "0" in the wallet/header. Throw so react-query retries and
      // consumers see pending/error (skeleton) instead of a fake zero.
      //
      // Tokens that only the backend advertises are the opposite case and must
      // NOT be fatal: SakerX's backend listed AIP's BSC *mainnet* address, which
      // has no code on testnet, so its balanceOf always reverts — treating that
      // as fatal threw away the user's real USDT balance along with it.
      const configured = new Set(BASE_TOKEN_CONFIG.map(c => c.address?.toLowerCase()).filter(Boolean));
      const fatal = result.find(
        (r, i) => r.status !== 'success' && configured.has(validTokens[i].baseTokenAddress?.toLowerCase())
      );
      if (fatal) throw new Error('token balance multicall failed', { cause: fatal.error });
      const validResultMap = new Map(validTokens.map((token, i) => [token.baseTokenAddress, result[i]]));
      return merged.map(token => {
        const entry = validResultMap.get(token.baseTokenAddress);
        return { ...token, balance: entry?.status === 'success' ? (entry.result as bigint) : 0n };
      });
    },
  });
  return query;
};
